import React, { useEffect, useState } from 'react';
import { Flex, Text, Grid, Icon, RadioGroup } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { saveCoverageSelection, selectCoverageSelection } from 'redux/features/plans/plansSlice';
import { coverageTypes, planPaymentMode, backgroundTypeEnum } from 'common/utils/enums';
import { BiSolidBadgeCheck } from 'react-icons/bi';
import { InfoDetail } from 'components/shared/member-info-card';
import { Accordion } from '@chakra-ui/react';
import FormatAsCurrency from 'common/utils/formatAsCurrency';
import { formatDateToHumanDate } from 'common/utils/formDateToHumanTime';
import { isWithinDateRange } from 'common/utils/dateRange';
import { getTerm } from 'common/utils/getTerm';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import { ApplyFormLayout } from 'common/layouts/apply-form';
import PaymentPlanCard from 'components/plans/payment-plan-card';
import { PrePurchaseCoverageCard } from 'components/coverages/coverage-card';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';

const PlanStepAdditionalCoverage = ({
  onBackClick,
  onNextClick,
  planData,
  stepNumber,
}) => {
  // HOOKS
  const { trackPageLoad } = useUserTracker();
  const dispatch = useDispatch();
  const coverageSelection = useSelector(selectCoverageSelection);
  const brand = planData?.property?.brand;
  const isDarkBg = brand?.backgroundType === backgroundTypeEnum.BACKGROUND_DARK;

  // SIDE EFFECTS
  useEffect(() => {
    trackPageLoad('PlanStepAdditionalCoverage');
  }, []);

  // Check if cancel anytime is available
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const cancelAnytime = planData?.coverage?.cancelAnytime;
  const cancelAnytimeEligible = React.useMemo(() => {
    const inRange = isWithinDateRange(
      {
        startDate: now,
        endDate: cancelAnytime?.endDate,
      },
      now
    );
    const length =
      getTerm(planData?.lease?.signDate, planData?.lease?.startDate) > 1;

    // If plan is tied to a property, check if cancel anytime is enabled
    let allowed = true;
    if (planData?.property) {
      allowed = planData?.property?.cancelAnytimeEnabled;
    }

    return inRange && length && allowed;
  }, [now, cancelAnytime, planData?.lease?.signDate, planData?.lease?.startDate, planData?.property]);

  // If cancel anytime is not available, skip this step
  useEffect(() => {
    if (!cancelAnytimeEligible) {
      onNextClick();
    }
  }, [cancelAnytimeEligible, onNextClick]);

  // Format amount helper
  const formatAmount = (amount) => {
    return FormatAsCurrency(amount, planData?.lease?.currency);
  };

  const cancelAnytimeAmount = cancelAnytime?.payment?.amount;
  const cancelAnytimeTotal = formatAmount(cancelAnytimeAmount);

  // Don't render if cancel anytime is not available
  if (!cancelAnytimeEligible) {
    return null;
  }

  return (
    <div className='flex flex-col w-full h-full'>
      <ApplyTitle
        title='Additional Coverage'
        subtitle="You're eligible for additional coverage that protects you before your lease begins."
        buttons={
          <ApplyButtons
            loading={false}
            stepNumber={stepNumber}
            isValid={true} // Always valid since user can choose "No thanks"
            handleSubmit={onNextClick}
            onNextClick={onNextClick}
            onBackClick={onBackClick}
            nextTitle='Continue to Payment'
            disableBack={!onBackClick}
          />
        }
      />

      {/* main content */}
      <ApplyContent>
        {planData?.property && (
          <ApplyFormLayout
            className={`p-6 rounded-lg border relative overflow-clip ${
              brand ? 'border-gray-200' : 'border-gray-200 bg-gray-50'
            }`}
            style={brand?.bgColor ? { backgroundColor: brand.bgColor } : {}}
          >
            <Flex className='z-10 relative flex-col sm:flex-row gap-6 sm:gap-8 col-span-2'>
              {brand && (
                <img
                  src={isDarkBg && brand?.logoAlt ? brand?.logo : brand?.logo}
                  alt={planData?.property?.name}
                  className='max-w-[140px] max-h-[50px] object-contain'
                />
              )}
              <Flex className='flex-col gap-1'>
                <Text
                  className={`font-medium text-lg ${
                    isDarkBg ? 'text-white' : 'text-black'
                  }`}
                >
                  ReLease Coverage at {planData?.property?.name}
                </Text>
                <Text
                  className={`text-base ${isDarkBg ? 'text-white opacity-60' : 'text-black opacity-60'}`}
                >
                  {planData?.property?.address?.streetAddress1},{' '}
                  {planData?.property?.address?.city},{' '}
                  {planData?.property?.address?.state}{' '}
                  {planData?.property?.address?.postalCode}
                </Text>
              </Flex>
            </Flex>
            {brand?.backgroundHeroImage && (
              <img
                src={brand?.backgroundHeroImage}
                alt={planData?.property?.name}
                className='absolute inset-0 opacity-15 z-0 object-cover'
              />
            )}
          </ApplyFormLayout>
        )}

        {/* container */}
        <Flex className='w-full flex-col gap-12'>
          {/* Cancel Anytime Coverage */}
          <Flex className='flex-col w-full rounded-md border border-gray-200'>
                <Flex className='flex-col gap-2 px-6 py-4'>
                  <h2 className='text-lg font-medium'>You're eligible for additional coverage</h2>
                  <div className='text-base text-gray-500'>
              <Text className='mb-1'>
                <span className='text-black'>It looks like your lease doesn't begin for a while. </span>
                You're eligible for additional coverage that protects
                you for the period between your lease signing date and
                lease start date, so you have added peace of mind in case your
                plans change.
              </Text>
            </div>
                </Flex>            
            <Accordion>
              <InfoDetail
                title='Coverage Period'
                subtitle={`${formatDateToHumanDate(
                  cancelAnytime?.startDate
                )} - ${formatDateToHumanDate(cancelAnytime?.endDate)}`}
              >
                <Text>
                  Your coverage period starts on{' '}
                  {formatDateToHumanDate(cancelAnytime?.startDate)} and
                  ends on {formatDateToHumanDate(cancelAnytime?.endDate)}.
                  You are eligible to cancel your lease during
                  this period and receive benefits.
                </Text>
              </InfoDetail>
              <InfoDetail
                title='Deductible'
                subtitle={
                  <span>
                    {FormatAsCurrency(
                      cancelAnytime?.deductible || 0,
                      'USD'
                    )}
                    <span className='text-gray-500'>
                      {' '}
                      (due at time of claim)
                    </span>
                  </span>
                }
              >
                <Text>
                  Your coverage includes a deductible in the amount of{' '}
                  {FormatAsCurrency(
                    cancelAnytime?.deductible || 0,
                    'USD'
                  )}
                  . This amount must be paid at the time of a claim before
                  ReLease begins covering the rest of your early
                  termination fees.
                </Text>
              </InfoDetail>
              <InfoDetail
                title='Notice Period'
                subtitle={
                  <span>
                    {cancelAnytime?.noticePeriod || 3} days{' '}
                    <span className='text-gray-500'>
                      (before move-out date)
                    </span>
                  </span>
                }
              >
                <Text>
                  You must provide at least {cancelAnytime?.noticePeriod || 3} days notice before your move-out date. 
                  This means you need to submit your claim at least {cancelAnytime?.noticePeriod || 3} days before 
                  the earliest date you want to move out.
                </Text>
              </InfoDetail>
            </Accordion>
          </Flex>

          <RadioGroup
                className='flex flex-col divide-y divide-gray-200 rounded-md border border-gray-200'
                value={coverageSelection[coverageTypes.CANCEL_ANYTIME]}
                onChange={(value) =>
                  dispatch(
                    saveCoverageSelection({
                      ...coverageSelection,
                      [coverageTypes.CANCEL_ANYTIME]: value,
                    })
                  )
                }
              >
                <PaymentPlanCard
                  title='Add additional coverage'
                  amount={cancelAnytimeTotal}
                  recurring={false}
                  startDate={`${formatDateToHumanDate(cancelAnytime?.startDate)} (today)`}
                  value={planPaymentMode.ONE_TIME}
                />
                <PaymentPlanCard
                  title='No thanks'
                  recurring={false}
                  value={' '}
                />
              </RadioGroup>
        </Flex>
      </ApplyContent>
    </div>
  );
};

export default PlanStepAdditionalCoverage; 